import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Link } from "react-router-dom";
import ChatAgent from "@/components/ChatAgent";
import SocialMediaLinks from "@/components/SocialMediaLinks";
import { 
  Building2, 
  Users, 
  FileText, 
  Trophy, 
  Calendar, 
  CheckCircle, 
  ArrowRight,
  Phone,
  Mail,
  MapPin,
  Instagram,
  Linkedin,
  Facebook,
  Twitter,
  Youtube
} from "lucide-react";

const Index = () => {
  const services = [
    {
      icon: Building2,
      title: "Trade License Registration",
      description: "Complete assistance with trade license applications and renewals",
      features: ["Document preparation", "Government liaison", "Fast processing"]
    },
    {
      icon: FileText,
      title: "Business Setup",
      description: "End-to-end business establishment and VAT application support",
      features: ["Company formation", "VAT registration", "Legal compliance", "Registration support"]
    },
    {
      icon: Trophy,
      title: "Sports Management Consultancy",
      description: "Sports club management with UAE Sports Council and Federation NOC support",
      features: ["Sports club setup", "UAE Sports Council NOC", "Sports Federation NOC", "Facilities management"]
    },
    {
      icon: Calendar,
      title: "Event Management",
      description: "Complete event management with permits, ticketing, and operational support",
      features: ["Event permits & licensing", "Ticketing & accreditation systems", "Security & cleaning planning", "Full event coordination"]
    },
    {
      icon: Users,
      title: "HR & Compliance",
      description: "UAE-compliant HR systems, policies, and organizational development",
      features: ["UAE-compliant HR policies", "Job grading & salary bands", "Appraisal systems", "HR templates & forms"]
    }
  ];

  return (
    <div className="min-h-screen bg-background">
      {/* Navigation */}
      <nav className="border-b bg-white/95 backdrop-blur supports-[backdrop-filter]:bg-white/60">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center space-x-3">
              <img 
                src="./images/logo.jpg" 
                alt="Company Logo" 
                className="h-12 w-auto object-contain"
              />
              <span className="text-3xl font-bold text-primary">Elite Edge Management Consultancy</span>
            </Link>
            <div className="hidden md:flex items-center space-x-6">
              <Link to="/" className="text-foreground hover:text-primary transition-colors">Home</Link>
              <Link to="/services" className="text-foreground hover:text-primary transition-colors">Services</Link>
              <Link to="/login" className="text-foreground hover:text-primary transition-colors">Admin</Link>
              <div className="flex items-center space-x-4">
                <SocialMediaLinks 
                  variant="header" 
                  iconSize="h-4 w-4" 
                  className="hidden lg:flex"
                />
              </div>
              <Button asChild>
                <Link to="/apply">Get Started</Link>
              </Button>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="py-20 px-4 bg-gradient-to-br from-primary/5 via-primary/10 to-accent/5">
        <div className="container mx-auto text-center">
          <Badge className="mb-4" variant="secondary">Elite Edge Management Consultancy</Badge>
          <h1 className="text-6xl md:text-7xl font-bold mb-6 bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">
            Your Business Success Partner
          </h1>
          <p className="text-xl text-muted-foreground mb-8 max-w-3xl mx-auto">
            We provide comprehensive business consultancy solutions that guide you through every stage of your entrepreneurial journey - 
            from initial company formation and regulatory compliance, through successful market launch, to sustainable operational excellence. 
            Our integrated approach ensures seamless business establishment, strategic growth, and long-term success in the UAE market.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button size="lg" asChild className="bg-gradient-to-r from-primary to-primary-glow">
              <Link to="/apply">
                Submit Your Application <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </Button>
            <Button size="lg" variant="outline" asChild>
              <Link to="/services">View Services</Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-20 px-4">
        <div className="container mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-4">Our Services</h2>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              Professional consultancy services tailored to your business needs
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((service, index) => {
              const Icon = service.icon;
              return (
                <Card key={index} className="group hover:shadow-lg transition-all duration-300 border-0 bg-gradient-to-br from-white to-gray-50">
                  <CardHeader>
                    <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center mb-4 group-hover:bg-primary/20 transition-colors">
                      <Icon className="h-6 w-6 text-primary" />
                    </div>
                    <CardTitle className="text-xl">{service.title}</CardTitle>
                    <CardDescription>{service.description}</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-2">
                      {service.features.map((feature, idx) => (
                        <li key={idx} className="flex items-center text-sm">
                          <CheckCircle className="h-4 w-4 text-green-500 mr-2" />
                          {feature}
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-20 px-4">
        <div className="container mx-auto text-center">
          <h2 className="text-4xl font-bold mb-4">Ready to Get Started?</h2>
          <p className="text-xl text-muted-foreground mb-12 max-w-2xl mx-auto">
            Contact us today to discuss your business needs and see how we can help you succeed.
          </p>
          <div className="grid md:grid-cols-3 gap-8 max-w-4xl mx-auto">
            <div className="flex flex-col items-center">
              <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mb-4">
                <Phone className="h-8 w-8 text-primary" />
              </div>
              <h3 className="font-semibold mb-2">Phone</h3>
              <p className="text-muted-foreground">+971585687738</p>
            </div>
            <div className="flex flex-col items-center">
              <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mb-4">
                <Mail className="h-8 w-8 text-primary" />
              </div>
              <h3 className="font-semibold mb-2">Email</h3>
              <p className="text-muted-foreground">info@eliteedgemc.ae</p>
            </div>
            <div className="flex flex-col items-center">
              <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mb-4">
                <MapPin className="h-8 w-8 text-primary" />
              </div>
              <h3 className="font-semibold mb-2">Location</h3>
              <p className="text-muted-foreground">UAE</p>
            </div>
          </div>
          
          {/* Social Media Section */}
          <div className="mt-16">
            <h3 className="text-2xl font-semibold mb-6">Connect With Us</h3>
            <p className="text-muted-foreground mb-8">Follow us on social media for updates, tips, and business insights</p>
            <SocialMediaLinks 
              variant="contact" 
              iconSize="h-8 w-8" 
              className="justify-center" 
              showLabels={true}
            />
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12 px-4">
        <div className="container mx-auto">
          <div className="grid md:grid-cols-4 gap-8">
            <div>
              <div className="flex items-center space-x-2 mb-4">
                <Building2 className="h-6 w-6" />
                <span className="text-xl font-bold">Elite Edge MC</span>
              </div>
              <p className="text-gray-400">
                Your trusted partner for business consultancy and trade license services.
              </p>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Services</h4>
              <ul className="space-y-2 text-gray-400">
                <li>Trade License Registration</li>
                <li>Business Setup</li>
                <li>Sports Management Consultancy</li>
                <li>Event Management</li>
                <li>HR & Compliance</li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4">CRM System</h4>
              <ul className="space-y-2 text-gray-400">
                <li>Client Management</li>
                <li>Quotation System</li>
                <li>Invoice Generation</li>
                <li>License Tracking</li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Contact</h4>
              <ul className="space-y-2 text-gray-400">
                <li className="flex items-center">
                  <Phone className="h-4 w-4 mr-2" />
                  +971585687738
                </li>
                <li className="flex items-center">
                  <Mail className="h-4 w-4 mr-2" />
                  info@eliteedgemc.ae
                </li>
                <li className="flex items-center">
                  <MapPin className="h-4 w-4 mr-2" />
                  UAE
                </li>
              </ul>
              
              {/* Social Media Links */}
              <div className="mt-6">
                <h5 className="font-semibold mb-3">Follow Us</h5>
                <SocialMediaLinks variant="footer" />
              </div>
            </div>
          </div>
          <div className="border-t border-gray-800 mt-8 pt-8 text-center text-gray-400">
            <p>&copy; 2024 Elite Edge Management Consultancy. All rights reserved.</p>
          </div>
        </div>
      </footer>
      
      {/* Chat Agent */}
      <ChatAgent />
    </div>
  );
};

export default Index;